import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { DIVISIONS, COMPANY } from "../lib/divisions";

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#0B2343] text-slate-200">
      {/* Top: Divisions grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Company */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/logo.png"
                alt="Noble Strategies Consulting"
                className="h-12 w-auto object-contain"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              {COMPANY.name}. Strategy. Evidence. Results — for veterans, families, and
              individuals nationwide.
            </p>
            <div className="mt-5 flex items-start gap-2 text-slate-400 text-sm">
              <MapPin size={16} className="text-[#C89B3C] mt-0.5 flex-shrink-0" />
              <span>Serving clients nationwide</span>
            </div>
          </div>

          {/* Veteran Services */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Veteran Services
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={DIVISIONS.veteran.phoneHref}
                  className="flex items-center gap-2 text-slate-300 hover:text-[#C89B3C] transition-colors"
                  data-testid="footer-veteran-phone"
                >
                  <Phone size={14} className="text-[#C89B3C]" />
                  {DIVISIONS.veteran.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${DIVISIONS.veteran.email}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-[#C89B3C] transition-colors break-all"
                  data-testid="footer-veteran-email"
                >
                  <Mail size={14} className="text-[#C89B3C] flex-shrink-0" />
                  <span className="truncate">{DIVISIONS.veteran.email}</span>
                </a>
              </li>
              <li className="pt-2">
                <Link
                  to="/veteran-services"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Learn more →
                </Link>
              </li>
            </ul>
          </div>

          {/* Insurance */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Insurance Solutions
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={DIVISIONS.insurance.phoneHref}
                  className="flex items-center gap-2 text-slate-300 hover:text-[#C89B3C] transition-colors"
                  data-testid="footer-insurance-phone"
                >
                  <Phone size={14} className="text-[#C89B3C]" />
                  {DIVISIONS.insurance.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${DIVISIONS.insurance.email}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-[#C89B3C] transition-colors"
                  data-testid="footer-insurance-email"
                >
                  <Mail size={14} className="text-[#C89B3C] flex-shrink-0" />
                  <span className="truncate">{DIVISIONS.insurance.email}</span>
                </a>
              </li>
              <li className="pt-2">
                <Link
                  to="/insurance"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Learn more →
                </Link>
              </li>
            </ul>
          </div>

          {/* Education */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Education Assistance
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={DIVISIONS.education.phoneHref}
                  className="flex items-center gap-2 text-slate-300 hover:text-[#C89B3C] transition-colors"
                  data-testid="footer-education-phone"
                >
                  <Phone size={14} className="text-[#C89B3C]" />
                  {DIVISIONS.education.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${DIVISIONS.education.email}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-[#C89B3C] transition-colors"
                  data-testid="footer-education-email"
                >
                  <Mail size={14} className="text-[#C89B3C] flex-shrink-0" />
                  <span className="truncate">{DIVISIONS.education.email}</span>
                </a>
              </li>
              <li className="pt-2">
                <Link
                  to="/education"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Learn more →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Site nav row */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/about" className="text-slate-400 hover:text-white transition-colors">
              About
            </Link>
            <Link to="/resources" className="text-slate-400 hover:text-white transition-colors">
              Resources
            </Link>
            <Link to="/contact" className="text-slate-400 hover:text-white transition-colors">
              Contact
            </Link>
            <Link to="/schedule" className="text-slate-400 hover:text-white transition-colors">
              Schedule
            </Link>
          </div>
          <div className="text-slate-500 text-xs">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </div>
        </div>

        {/* Secure Access */}
        <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          <span className="text-[#C89B3C] font-semibold uppercase tracking-wider">
            Secure Access
          </span>
          <a
            href="/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-agent-login"
            className="text-slate-400 hover:text-white transition-colors"
          >
            Agent Login
          </a>
          <a
            href={process.env.REACT_APP_PROVIDER_PORTAL_URL || "https://provider-nexus-3.emergent.host"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-provider-portal"
            className="text-slate-400 hover:text-white transition-colors"
          >
            Provider Portal
          </a>
        </div>

        {/* Legal links */}
        <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          <Link to="/privacy" className="text-slate-500 hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms" className="text-slate-500 hover:text-white transition-colors">
            Terms of Service
          </Link>
          <Link to="/disclaimer" className="text-slate-500 hover:text-white transition-colors">
            Disclaimer
          </Link>
          <Link to="/cookies" className="text-slate-500 hover:text-white transition-colors">
            Cookie Notice
          </Link>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-white/10 bg-[#081B36]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
          <h4 className="text-[#C89B3C] font-semibold text-xs uppercase tracking-wider mb-3">
            Important Disclaimer
          </h4>
          <div className="space-y-2 text-slate-400 text-xs leading-relaxed">
            <p>
              <strong className="text-slate-300">Not Legal Advice:</strong> {COMPANY.name}{" "}
              provides consulting and advocacy services only. We are not a law firm and do not
              provide legal advice.
            </p>
            <p>
              <strong className="text-slate-300">Not Affiliated with the VA:</strong> We are an
              independent, veteran-owned business, not affiliated with, endorsed by, or connected
              to the U.S. Department of Veterans Affairs.
            </p>
            <p>
              <strong className="text-slate-300">No Guarantee of Results:</strong> We cannot
              guarantee specific outcomes, VA rating decisions, insurance approvals, or
              educational benefit awards.
            </p>
            <p>
              <strong className="text-slate-300">Accreditation:</strong> Our consultants are not
              accredited by the VA Office of General Counsel to represent veterans before the VA.
              We are not licensed attorneys or healthcare providers.
            </p>
            <p className="pt-2 text-slate-500">
              By using our services, you acknowledge that you have read and understood this
              disclaimer.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
